/// FOURJS_START_COPYRIGHT(D,2026)
/// Property of Four Js*
/// (c) Copyright Four Js 2026, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

"use strict";

modulum("AIProviderBase", function (context, cls) {
  /**
   * Base class for AI providers
   * @class AIProviderBase
   * @memberOf classes
   */
  class AIProviderBase /** @lends classes.AIProviderBase.prototype */ {
    /**
     * Registered AI provider classes indexed by provider id
     */
    static _registeredProviders = new Map();

    /**
     * Register an AI provider class
     * @param {Function} ProviderClass
     */
    static registerProvider(ProviderClass) {
      const provider = new ProviderClass();
      if (!provider.id) {
        console.warn("AIProviderBase: invalid provider registration", ProviderClass);
        return;
      }
      this._registeredProviders.set(provider.id, ProviderClass);
    }

    /**
     * Create all registered AI provider instances
     * @returns {classes.AIProviderBase[]}
     */
    static createRegisteredProviders() {
      return Array.from(this._registeredProviders.values()).map((ProviderClass) => new ProviderClass());
    }

    /**
     * @param {string} id - Provider identifier (ex: "openai", "ollama")
     * @param {string} displayName - Human readable name
     * @param {string} location - "remote" | "chrome-local"
     * @param {Object} [capabilities={}]
     */
    constructor(id, displayName, location, capabilities = {}) {
      this.id = id;
      this.displayName = displayName;
      this.location = location;
      this.capabilities = capabilities || {};
    }

    /**
     * Provider info for UI listing
     * @returns {{id:string,label:string,location:string}}
     */
    getInfo() {
      return {
        id: this.id,
        label: this.displayName,
        location: this.location
      };
    }

    /**
     * Indicates whether the provider is available in the current runtime.
     * For remote providers, when the GAS advertises the "ai" capability,
     * the provider must also be registered on the GAS side.
     * Override in subclasses for runtime-specific checks (browser API, etc.).
     * @returns {boolean}
     */
    isAvailable() {
      if (this.location === "remote" && gbc.AIService.hasGasAiCapability()) {
        return gbc.AIService.getGasProviders().includes(this.id);
      }
      return true;
    }

    /**
     * Whether AI requests are routed through the GAS proxy
     * @returns {boolean}
     * @protected
     */
    _isViaGas() {
      return gbc.AIService.hasGasAiCapability();
    }

    /**
     * Direct (non-GAS) API host for this provider
     * @returns {string|null}
     * @protected
     */
    _getDirectApiHost() {
      return null;
    }

    /**
     * Builds the API URL routing through the GAS proxy or directly depending on `_isViaGas()`
     * @param {string} path
     * @returns {string}
     * @protected
     */
    _buildApiUrl(path) {
      if (this._isViaGas()) {
        const sessionId = context.SessionService.getCurrent().getSessionId();
        return `/ua/ai/${sessionId}/${this.id}/${path}`;
      }
      return `${this._getDirectApiHost()}/${path}`;
    }

    /**
     * Returns `$(api_key)` in GAS mode (substituted server-side) or the user-provided key otherwise
     * @param {string} apiKey
     * @returns {string}
     * @protected
     */
    _resolveApiKey(apiKey) {
      return this._isViaGas() ? "$(api_key)" : apiKey;
    }

    /**
     * Model id for the given feature with this provider, read from `gbc-ai-<feature>-<this.id>-model`
     * @param {string} feature "rewrite" | "conversation"
     * @returns {string|null}
     * @protected
     */
    _getModel(feature) {
      return context.ThemeService.getValue(`gbc-ai-${feature}-${this.id}-model`);
    }

    /**
     * Performs an HTTP request to the provider API.
     * @param {string} url
     * @param {Object} options - { method, headers, body }
     * @returns {Promise<Object|null>} parsed JSON response
     * @protected
     */
    async _request(url, { method = "GET", headers = {}, body = null } = {}) {
      const fetchOptions = {
        method,
        headers
      };

      if (body) {
        fetchOptions.body = JSON.stringify(body);
      }

      const res = await fetch(url, fetchOptions);

      if (!res.ok) {
        throw new Error(`${this.id} API error (${res.status})`);
      }

      const text = await res.text();
      if (!text) {
        return null;
      }

      return JSON.parse(text);
    }

    //#region Rewrite

    /**
     * Rewrite text using provider
     * Override in subclasses
     * @param {string} text
     * @param {Object} options
     * @returns {Promise<string|null>}
     */
    async rewrite(text, options) {
      return null;
    }

    /**
     * Normalizes rewrite input text.
     * Returns `null` when the input is null/undefined or empty after trimming.
     * @param {*} text
     * @returns {string|null}
     * @protected
     */
    _prepareRewriteInput(text) {
      if (text === null || text === undefined) {
        return null;
      }

      const input = String(text).trim();
      if (!input) {
        return null;
      }

      return input;
    }

    /**
     * Returns a rewrite theme value with provider-specific
     * @param {string} suffix
     * @returns {string}
     * @protected
     */
    _getRewriteThemeValue(suffix) {
      const providerKey = `gbc-ai-rewrite-${this.id}-${suffix}`;
      const providerValue = context.ThemeService.getValue(providerKey);
      if (providerValue) {
        return providerValue;
      }

      const defaultKey = `gbc-ai-rewrite-${suffix}`;
      return context.ThemeService.getValue(defaultKey);
    }

    /**
     * Builds rewrite prompt instructions shared by remote providers
     * @param {Object} [options={}]
     * @returns {string}
     * @protected
     */
    _buildRewritePrompt() {
      return this._getRewriteThemeValue("instructions").replaceAll("{{BR}}", "\n");
    }

    /**
     * Validates rewrite input and returns a normalized string
     * @param {*} text - Raw text to rewrite
     * @param {Object} options - Rewrite options
     * @returns {string|null}
     * @protected
     */
    _buildRewriteInput(text, options = {}) {
      const tone = options.tone || "as-is";
      const length = options.length || "as-is";

      const toneMap = {
        "as-is": context.ThemeService.getValue("gbc-ai-rewrite-tone-as-is"),
        "more-formal": context.ThemeService.getValue("gbc-ai-rewrite-tone-more-formal"),
        "more-casual": context.ThemeService.getValue("gbc-ai-rewrite-tone-more-casual")
      };
      const lengthMap = {
        "as-is": context.ThemeService.getValue("gbc-ai-rewrite-length-as-is"),
        shorter: context.ThemeService.getValue("gbc-ai-rewrite-length-shorter"),
        longer: context.ThemeService.getValue("gbc-ai-rewrite-length-longer")
      };

      const toneLine = toneMap[tone] || "";
      const lengthLine = lengthMap[length] || "";
      const localeLines = this._formatLocale(options.contextLocale);
      const contextLines = this._formatContextForm(options.contextForm);

      const template = this._getRewriteThemeValue("input-template");

      let out = template
        .replaceAll("{{BR}}", "\n")
        .replace("{{locale}}", localeLines)
        .replace("{{context}}", contextLines)
        .replace("{{tone}}", toneLine)
        .replace("{{length}}", lengthLine)
        .replace("{{text}}", text);

      return out;
    }

    /**
     * Format locale metadata as a `key=value` plain text block, one entry per line
     * @param {?Object} localeData
     * @returns {string}
     * @protected
     */
    _formatLocale(localeData) {
      if (!localeData) {
        return "";
      }
      const parts = [];
      if (localeData.dateFormat) {
        parts.push(`date_format=${localeData.dateFormat}`);
      }
      if (localeData.decimalSeparator) {
        parts.push(`decimal_separator=${localeData.decimalSeparator}`);
      }
      if (localeData.thousandsSeparator) {
        parts.push(`thousands_separator=${localeData.thousandsSeparator}`);
      }
      return parts.join("\n");
    }

    /**
     * Format context form items as a "name(desc)=value" block, one entry per line
     * @param {Array.<Object>} contextForm
     * @returns {string}
     * @protected
     */
    _formatContextForm(contextForm) {
      if (!Array.isArray(contextForm)) {
        return "";
      }
      return contextForm
        .filter((item) => item && item.name)
        .map((item) => {
          const title = item.title ? ` (${item.title})` : "";
          return `${item.name}${title}=${item.value ?? ""}`;
        })
        .join("\n");
    }
    //#endregion

    //#region Conversation

    /**
     * Creates a new conversation.
     * Returns provider-specific data to be stored in ConversationState.providerData.
     * Override in subclasses that support conversations.
     * @param {Object} options - { apiKey, model, ... }
     * @returns {Promise<Object|null>} providerData object, or null on failure
     */
    async createConversation(options) {
      return null;
    }

    /**
     * Sends a user message in an existing conversation.
     * Returns the updated providerData and the assistant response text.
     * Override in subclasses that support conversations.
     * @param {Object} state - Current ConversationState
     * @param {string} userMessage
     * @param {Object} options - { apiKey, model, ... }
     * @returns {Promise<{providerData: Object, response: string}|null>}
     */
    async sendMessage(state, userMessage, options) {
      return null;
    }

    /**
     * Deletes a conversation server-side.
     * No-op by default — override in providers that manage server-side state.
     * @param {Object} state - Current ConversationState
     * @param {Object} options - { apiKey, ... }
     * @returns {Promise<void>}
     */
    async deleteConversation(state, options) {
      // no-op by default
    }
    //#endregion
  }

  cls.AIProviderBase = AIProviderBase;
});
